import { type SyntheticEvent, useState } from "react";
import { Box, Column, Text, HtImage } from "@hightouch/htui";
import { AttachmentTypeTile } from "src/components/llm/components/attachment-type-tile";
import {
  attachmentCategory,
  attachmentPresentation,
} from "src/components/llm/utils/attachment-presentation";
import {
  fileExtensionLabel,
  fileTypeDescription,
  isImage,
  isVideo,
  resolveAttachmentMimeType,
} from "src/components/llm/utils/file-processing";

/**
 * Longer than this and the label can't fit inside a 32px pill, so the icon
 * carries it alone. Only ".markdown" is affected; the popover shows the full
 * filename regardless.
 */
const MAX_THUMBNAIL_LABEL_LENGTH = 4;

/**
 * `thumbnail` fills a 32px composer pill, `tile` an 84px context tile, and
 * `preview` the 200px hover popover.
 */
export type AttachmentMediaVariant = "thumbnail" | "tile" | "preview";

const TYPE_TILE_GLYPH_SIZES = { thumbnail: 3, tile: 6 } as const;

type AttachmentMediaProps = {
  fileName: string;
  mimeType: string;
  /** The file itself. Drawn only for images and video. */
  src?: string | null;
  /** A still for a video, so the clip is not fetched to paint a frame. */
  posterUrl?: string;
  variant: AttachmentMediaVariant;
};

/**
 * What an attachment looks like wherever the chat shows it: the image, a
 * video's first frame, or a tile that names the file type. A video that fails
 * to load falls back to the type tile.
 */
export const AttachmentMedia = ({
  fileName,
  mimeType: reportedMimeType,
  src,
  posterUrl,
  variant,
}: AttachmentMediaProps) => {
  const [failedVideoSrc, setFailedVideoSrc] = useState<string | null>(null);
  // A picked library asset can carry no recorded type, so the extension decides.
  const mimeType = resolveAttachmentMimeType(reportedMimeType, fileName);
  const isVideoFile = isVideo(mimeType, fileName);

  if (src && isImage(mimeType)) {
    return (
      <HtImage
        alt={fileName}
        src={src}
        width="100%"
        height="100%"
        objectFit="cover"
      />
    );
  }

  if (isVideoFile && posterUrl) {
    return (
      <HtImage
        alt={fileName}
        src={posterUrl}
        width="100%"
        height="100%"
        objectFit="cover"
      />
    );
  }

  if (isVideoFile && src && failedVideoSrc !== src) {
    // autoPlay + muted + playsInline forces a visible frame without playback
    // controls (preload="metadata" alone often paints a blank/black tile on
    // Safari and some codecs). Pausing once the first frame is decoded turns
    // the element into a static poster instead of playing the whole clip.
    return (
      <Box
        as="video"
        src={src}
        muted
        playsInline
        autoPlay
        preload="metadata"
        width="100%"
        height="100%"
        sx={{ objectFit: "cover" }}
        onLoadedData={(e: SyntheticEvent<HTMLElement>) => {
          // Box types the event target as a div; narrow to reach pause().
          if (e.currentTarget instanceof HTMLVideoElement) {
            e.currentTarget.pause();
          }
        }}
        onError={() => setFailedVideoSrc(src)}
      />
    );
  }

  // Files with nothing to show name their format instead: a row of identical
  // grey file icons tells the user nothing about what they attached.
  return (
    <FileTypeTile fileName={fileName} mimeType={mimeType} variant={variant} />
  );
};

const FileTypeTile = ({
  fileName,
  mimeType,
  variant,
}: {
  fileName: string;
  mimeType: string;
  variant: AttachmentMediaVariant;
}) => {
  const category = attachmentCategory(mimeType, fileName);

  // The pill itself is the tile, so the category tints it edge to edge and the
  // glyph sits above its extension. The shades mirror IconBox's default
  // variant, which draws the same category everywhere else, so a composer
  // thumbnail and a sent chip agree on color.
  if (variant !== "preview") {
    const { icon: Icon, color } = attachmentPresentation(category);
    const extension = fileExtensionLabel(fileName);
    const showExtension =
      extension.length > 0 &&
      (variant === "tile" || extension.length <= MAX_THUMBNAIL_LABEL_LENGTH);

    return (
      <Column
        align="center"
        justify="center"
        gap={variant === "tile" ? 1 : "1px"}
        width="100%"
        height="100%"
        bg={`${color}.200`}
      >
        <Box
          as={Icon}
          boxSize={TYPE_TILE_GLYPH_SIZES[variant]}
          color={`${color}.600`}
          flexShrink={0}
        />
        {showExtension &&
          (variant === "tile" ? (
            <Text size="sm" fontWeight="semibold" color={`${color}.600`}>
              {extension}
            </Text>
          ) : (
            // Below the type scale: the label has to fit inside a 32px pill.
            <Box
              color={`${color}.600`}
              fontWeight="semibold"
              sx={{ fontSize: "7px", lineHeight: 1 }}
            >
              {extension}
            </Box>
          ))}
      </Column>
    );
  }

  return (
    <Column
      align="center"
      justify="center"
      gap={2}
      width="100%"
      height="100%"
      bg="base.background"
    >
      <AttachmentTypeTile category={category} size="xl" />
      <Text size="sm" color="text.secondary">
        {fileTypeDescription(mimeType)}
      </Text>
    </Column>
  );
};
